import React, { useRef, useEffect, useState } from "react";
import {
    Box,
    IconButton,
    Avatar,
    Typography,
    Tooltip,
} from "@mui/material";
import { AddAPhoto, Close } from "@mui/icons-material";
import { SxProps } from "@mui/system";
import { useSnackBar } from "../ToastProvider";

interface ImageUploaderProps {
    images: (File | string)[] | null;
    onChange: (files: (File | string)[]) => void;
    multiple?: boolean;
    maxCount?: number;
    showRemoveButton?: boolean;
    disabled?: boolean;
    size?: number;
    sx?: SxProps;
    onImageClick?: (index: number) => void;
}

const FALLBACK_IMG = "/default.jpg";

const ImageUploader: React.FC<ImageUploaderProps> = ({
    images,
    onChange,
    multiple = false,
    maxCount = 5,
    showRemoveButton = true,
    disabled = false,
    size = 100,
    sx,
    onImageClick,
}) => {
    const { showSnackBar } = useSnackBar();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [failed, setFailed] = useState<Record<string, boolean>>({});
    const objectUrlsRef = useRef<string[]>([]);
    const [urls, setUrls] = useState<Record<string, string>>({});

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            objectUrlsRef.current = [];
        };
    }, []);

    useEffect(() => {
        // Cleanup old URLs if images array changes (especially when removing)
        return () => {
            Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
        };
    }, [urls]);

    useEffect(() => {
        if (!images) return;
        const newUrls: Record<string, string> = {};
        images.forEach((item) => {
            if (item instanceof File) {
                const key = item.name + item.lastModified;
                if (!urls[key]) newUrls[key] = URL.createObjectURL(item);
            }
        });
        if (Object.keys(newUrls).length)
            setUrls((prev) => ({ ...prev, ...newUrls }));
    }, [images]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        if (!files.length) return;

        const currentCount = images?.length || 0;
        const remaining = maxCount - currentCount;

        // Prevent selecting more than allowed
        if (files.length > remaining) {
            showSnackBar(`You can only add ${remaining} ${remaining < maxCount ? "more" : ""} image${remaining > 1 ? "s" : ""}`, "error");
            e.target.value = ""; // reset input
            return;
        }

        const updated = multiple
            ? [...(images || []), ...files]
            : files.slice(0, 1);

        onChange(updated);
        e.target.value = "";
    };

    const handleRemove = (index: number) => {
        if (!images) return;
        const updated = images.filter((_, i) => i !== index);
        onChange(updated.length ? updated : []);
    };

    const openFileDialog = () => {
        if (!disabled) inputRef.current?.click();
    };

    const getFileName = (item: File | string): string =>
        typeof item === "string"
            ? item.split("/").pop() || "image"
            : item.name;

    const renderAddPlaceholder = (key: string | number) => (
        <Box
            key={key}
            sx={{
                width: size,
                height: size,
                border: "2px dashed #ccc",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer",
                transition: "0.2s",
                "&:hover": !disabled
                    ? { borderColor: "#1976d2", bgcolor: "#f5faff" }
                    : {},
                position: "relative",
                ...sx,
            }}
            onClick={openFileDialog}
        >
            <AddAPhoto sx={{ fontSize: size / 2.5, color: "#888" }} />
        </Box>
    );

    const renderPreview = (item: File | string, index: number) => {
        const key = typeof item === "string" ? item : item.name + item.lastModified;
        const src =
            failed[key] ? FALLBACK_IMG :
                typeof item === "string" ? item : urls[key] || FALLBACK_IMG;

        const filename = getFileName(item);

        return (
            <Box
                key={key}
                sx={{
                    width: size,
                    height: size,
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: onImageClick ? "pointer" : "default",
                    border: "1px solid #e0e0e0",
                    "&:hover .remove-btn": { opacity: 1 },
                    ...sx,
                }}
            >
                <Avatar
                    src={src}
                    variant="square"
                    slotProps={{
                        img: {
                        onError: () => setFailed((p) => ({ ...p, [key]: true })),
                        },
                    }}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                    }}
                    onClick={() => onImageClick?.(index)}
                >
                    {" "}
                </Avatar>

                {showRemoveButton && !disabled && (
                    <IconButton
                        className="remove-btn"
                        size="small"
                        onClick={() => handleRemove(index)}
                        sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            opacity: 0,
                            transition: "opacity 0.2s",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                )}

                <Tooltip title={filename}>
                    <Typography
                        variant="caption"
                        sx={{
                            position: "absolute",
                            bottom: 2,
                            left: 0,
                            width: "100%",
                            textAlign: "center",
                            color: "#fff",
                            fontSize: "0.7rem",
                            background: "rgba(0,0,0,0.4)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            px: 0.5,
                        }}
                    >
                        {filename}
                    </Typography>
                </Tooltip>
            </Box>
        );
    };

    const canAddMore = multiple && (!images || images.length < maxCount);

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
            }}
        >
            {images?.map(renderPreview)}
            {(canAddMore || !multiple) && renderAddPlaceholder("add-btn")}
            <input
                type="file"
                accept="image/*"
                multiple={multiple}
                ref={inputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
                disabled={disabled}
            />
        </Box>
    );
};

export default ImageUploader;
